import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { STATUS_CONFIG } from "@/lib/format";
import { CheckCircle2, Clock, AlertCircle, Minus, ThumbsUp, HelpCircle, XCircle, Loader2 } from "lucide-react";

const ICONS = { check: CheckCircle2, clock: Clock, alert: AlertCircle, minus: Minus, x: XCircle };

export default function WorkerPublic() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/public/worker/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, [id]);

  const respond = async (resp) => {
    setSending(true);
    try { await api.post(`/workers/${id}/response`, { response: resp }); toast.success("¡Gracias! Registramos tu respuesta."); load(); }
    catch (e) { toast.error("No se pudo registrar tu respuesta"); }
    finally { setSending(false); }
  };

  if (data === null) return <div className="min-h-screen flex items-center justify-center bg-slate-100"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (data === false) return <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-500">Enlace no válido.</div>;

  return (
    <div className="min-h-screen bg-slate-100 flex justify-center py-6 px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="bg-[#0B132B] text-white rounded-2xl p-5 flex items-center gap-3">
          <div className="bg-teal-500/20 text-teal-400 p-2 rounded-full"><CheckCircle2 size={22} /></div>
          <div><p className="font-display font-bold">Onboarding Follow-Up</p><p className="text-xs text-slate-400">{data.company}</p></div>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mt-4">
          <div className="flex items-center justify-between">
            <div><h1 className="font-display text-xl font-bold text-slate-900">Mis pendientes</h1><p className="text-sm text-slate-500">Hola {data.first_name}, este es tu avance:</p></div>
            <span className="text-2xl font-display font-bold text-blue-600">{data.progress_percentage}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden mt-3"><div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${data.progress_percentage}%` }} /></div>

          <div className="mt-5 space-y-2">
            {data.requirements.map((r) => {
              const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.PENDING;
              const Icon = ICONS[cfg.icon] || AlertCircle;
              return (
                <div key={r.name} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                  <div className={`h-8 w-8 rounded-lg ${cfg.bg} flex items-center justify-center`}><Icon size={17} className={cfg.color} /></div>
                  <div className="flex-1"><p className="text-sm font-medium text-slate-800">{r.name}</p><p className="text-xs text-slate-400">{cfg.label}</p></div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Message */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mt-4">
          <p className="text-xs font-semibold text-blue-600 mb-2">Mensaje de RR.HH.</p>
          <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{data.message}</p>
        </div>

        {/* Actions */}
        <div className="space-y-2 mt-4">
          <button data-testid="worker-response-continue-button" disabled={sending} onClick={() => respond("CONTINUE")} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60"><ThumbsUp size={18} /> Quiero continuar</button>
          <button data-testid="worker-response-help-button" disabled={sending} onClick={() => respond("NEEDS_HELP")} className="w-full bg-white border border-slate-300 text-slate-700 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-slate-50 disabled:opacity-60"><HelpCircle size={18} /> Necesito ayuda</button>
          <button data-testid="worker-response-decline-button" disabled={sending} onClick={() => respond("WITHDRAW")} className="w-full bg-white border border-red-300 text-red-600 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-red-50 disabled:opacity-60"><XCircle size={18} /> No continuaré</button>
        </div>

        {data.intent_status && data.intent_status !== "NONE" && (
          <p className="text-center text-xs text-slate-400 mt-3">Última respuesta registrada: {data.intent_status}</p>
        )}
        <p className="text-center text-xs text-slate-400 mt-4">Personas que comienzan bien, llegan más lejos.</p>
      </div>
    </div>
  );
}
